import {
  useState,
  useCallback,
  useLayoutEffect,
  FC,
  ComponentProps,
  useRef,
} from 'react'
import { Slider } from 'antd'
import { PlusCircleOutlined, MinusCircleOutlined } from '@ant-design/icons'
import { Modal } from 'antd'
import Cropper, { Area, Point } from 'react-easy-crop'
import LoadingImg from '@/icons/LoadingImg'
import { twMerge } from 'tailwind-merge'
import useI18n from '@/hooks/useI18n'
import { CloseOutlinedIcon } from '@/components/svg'

interface AvatarEditorItemOutputProps extends ComponentProps<'div'> {
  size: number
  image: string
  croppedArea: Area | null
  loading?: boolean
}

export const AvatarEditorItemOutput: FC<AvatarEditorItemOutputProps> = ({
  size,
  image,
  croppedArea,
  loading,
  className,
}) => {
  const { t } = useI18n()
  if (!croppedArea) {
    return (
      <div
        className={twMerge(
          'relative overflow-hidden rounded-full border border-white',
          className
        )}
        style={{ width: size, height: size }}
      >
        <img
          className="h-full w-full object-cover"
          src={image}
          alt={t('Avatar')}
        />
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50">
            <LoadingImg className="scale-[.8]" />
          </div>
        )}
      </div>
    )
  }
  const scale = 100 / croppedArea.width
  const transform = {
    x: `${-croppedArea.x * scale}%`,
    y: `${-croppedArea.y * scale}%`,
    scale,
    width: 'calc(100% + 0.5px)',
    height: 'auto',
  }
  const imageStyle = {
    transform: `translate3d(${transform.x}, ${transform.y}, 0) scale3d(${transform.scale},${transform.scale},1)`,
    width: transform.width,
    height: transform.height,
  }
  return (
    <div
      className={twMerge(
        'relative overflow-hidden rounded-full border border-white',
        className
      )}
      style={{ width: size, height: size }}
    >
      <img
        className="absolute left-0 top-0 origin-top-left"
        src={image}
        alt={t('Avatar')}
        style={imageStyle}
      />
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50">
          <LoadingImg className="scale-[.8]" />
        </div>
      )}
    </div>
  )
}

interface IAvatarEditorProps {
  image: string
  open: boolean
  title: string
  onOk: (croppedArea: Area | null, croppedAreaPixels: Area | null) => void
  onCancel: () => void
  initialCroppedAreaPixels: Area | null
}

export default function AvatarEditorItem({
  image,
  open,
  title,
  initialCroppedAreaPixels,
  onOk,
  onCancel,
}: IAvatarEditorProps) {
  const { t } = useI18n()
  const ref = useRef<Cropper>(null)
  const [croppedArea, setCroppedArea] = useState<Area | null>(null)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(
    initialCroppedAreaPixels
  )
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)

  const onCropComplete = (croppedArea: Area, croppedAreaPixels: Area) => {
    setCroppedArea(croppedArea)
    setCroppedAreaPixels(croppedAreaPixels)
  }

  const handleDecrease = useCallback((step: number = 1) => {
    setZoom((scale) => {
      const newScale = scale - step
      if (newScale < 1) {
        return 1
      }
      return newScale
    })
  }, [])

  const handleIncrease = useCallback((step: number = 1) => {
    setZoom((scale) => {
      const newScale = scale + step
      if (newScale > 5) {
        return 5
      }
      return newScale
    })
  }, [])

  const handleScaleChange = (value: number) => {
    setZoom(value)
  }

  const handleApply = useCallback(() => {
    if (croppedAreaPixels) {
      onOk(croppedArea, croppedAreaPixels)
      return
    }
    onCancel()
  }, [croppedArea, croppedAreaPixels, onCancel, onOk])

  // 解决 Cropper 组件是类组件导致关闭时crop会重置到原点的问题 TODO: 创建角色第一次裁剪没问题，第二次裁剪有问题，建议换一个库然后删掉useLayoutEffect里的逻辑
  useLayoutEffect(() => {
    // 解决第一次打开编辑器，放大图片然后关闭（不保存），再次打开编辑器时，zoom放大不恢复的问题
    setZoom(1)
    ref.current?.forceUpdate()
  }, [open])
  return (
    <Modal
      open={open}
      centered
      maskClosable={false}
      footer={null}
      width={511}
      closeIcon={<CloseOutlinedIcon strokeWidth={2} size={18} />}
      onCancel={onCancel}
      styles={{
        content: {
          padding: 0,
          overflow: 'hidden',
          borderRadius: 16,
          background: 'rgba(11, 2, 15, 0.72)',
          boxShadow: '0 24px 80px rgba(0, 0, 0, 0.55)',
          backdropFilter: 'blur(14px)',
        },
        body: {
          background: 'transparent',
        },
        mask: {
          backgroundColor: 'rgba(0, 0, 0, 0.4)',
        },
      }}
      rootClassName="max-md:!hidden"
    >
      <div className="flex h-[610px] flex-col px-6 py-4 text-white">
        <div className="relative h-[18px] shrink-0 text-center text-sm leading-[14px] text-white">
          {title}
        </div>
        <div className="mt-3 flex min-h-0 flex-1 flex-col">
          <div className="relative mx-auto h-[463px] w-[463px] max-w-full overflow-hidden bg-black/30">
            <Cropper
              ref={ref}
              image={image}
              crop={crop}
              zoom={zoom}
              maxZoom={5}
              aspect={1}
              cropShape="round"
              showGrid={false}
              onCropChange={setCrop}
              onCropComplete={onCropComplete}
              onZoomChange={setZoom}
              initialCroppedAreaPixels={undefined}
            />
          </div>
          <div className="mt-[18px] flex h-8 items-center gap-3 text-white">
            <MinusCircleOutlined
              className="shrink-0 cursor-pointer text-[18px] text-white/90"
              onClick={() => handleDecrease(1)}
            />
            <Slider
              min={1}
              max={5}
              value={zoom}
              step={0.1}
              className="flex-1"
              style={{ margin: 0 }}
              onChange={handleScaleChange}
              trackStyle={{ backgroundColor: '#FFFFFF' }}
              railStyle={{ backgroundColor: 'rgba(255,255,255,0.45)' }}
            />
            <PlusCircleOutlined
              className="shrink-0 cursor-pointer text-[18px] text-white/90"
              onClick={() => handleIncrease(1)}
            />
          </div>
          <div className="mt-6 flex justify-end">
            <button
              type="button"
              className="rounded-full bg-white px-5 py-2 text-sm font-medium text-black transition hover:bg-white/90"
              onClick={handleApply}
            >
              {t('Confirm')}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  )
}
